
// Modules
import * as THREE from "three"
import gsap from "gsap"

// HelpersThree
import Cylinder2 from '../Cylinder2'
import Cylinder2Old from '../Cylinder2Old'
import SceneWithRaycaster from "./SceneWithRaycaster"
import Scene3d from "../Scene3d"

// Utils
import params from '../../../utils/params'
import { AxesHelper } from "three"
import Utils from "../../../utils/utils"

// Constants
const ROTATION_Y_SPEED =  .001

const SLOGAN_WORDING = {
    ubiquity: "UBIQUITY",
    weCreate: "WE CREATE BRAND, MUSIC & CREATIVE CONTENT",
    weAre :"WE ARE FRENCH PRODUCTION COMPANY",
}

const INTERSECTABLE_CLASS = Object.freeze({
    members: "members",
    links: "links",
})

const TYPOGRAPHY_FOR_ROW = {
    "fullname":  {
        fontWeight: 700,
        fontFamily: "GrtskTeraBold"
    },
    "job":  {
        fontWeight: 400,
        fontFamily: "GrtskPetaRegular"
    },
    "mail":  {
        fontWeight: 400,
        fontFamily: "GrtskPetaRegular"
    }
}
const CYLINDER_RADIAL_SEGMENTS = 256
const CYLINDER_MEMBER_POINTER_EVENT_NAME = "CYLINDER_MEMBER_POINTER_EVENT_NAME"

let sceneContactClearColor = Utils.isSafari() ? null : new THREE.Color(1, 1, 1)

export default class SceneContact extends SceneWithRaycaster{
    constructor(container, parameters, data){
        super(container, {
            ...parameters,
            clearColor: sceneContactClearColor,
            cameraFov: 65
        }, data)

        this.data.contacts = data.contacts
        this.data.mobileDetected = data.mobileDetected

        // Constants
        this.CYLINDER_RADIUS = 38
        this.CYLINDER_RATIO = this.data.mobileDetected ? 75 : 55

        // unvariables
        this._origin = new THREE.Vector3()

        // public variables
        this._wordsCoordinates = null
        this._cylinderOffset = 0
        this._cylinderOffsetInMembers = 0
        this._cameraTarget = this.getCameraTarget()
        this._cylinderMembersGroup = new THREE.Group()
        this._rotatingCylinders = []
        
        this._cylinderMemberInstances = []
        this._colorForHoveredMember = new THREE.Color()

        this._isDragging = false
        this._dragMouseCurrent = new THREE.Vector2()

        // bind
        
        this.onDragStart = this.onDragStart.bind(this)
        this.onDragMove = this.onDragMove.bind(this)
        this.onDragStop = this.onDragStop.bind(this)

        this._onMouseUpMesh = this._onMouseUpMesh.bind(this)
        this._onMouseDownMesh = this._onMouseDownMesh.bind(this)

        window.SceneContact = this
        
    }

    /** CORE */

    start(){
        this._animateEntrance()
        super.start()
    }

    /** Animation */

    _animateEntrance(){
        gsap.from(this.container, {
            duration: .6,
            opacity: 0
        })
    }

    _animateExit(onComplete){
        gsap.to(this.container, {
            duration: .6,
            onComplete: onComplete,
            opacity: 0
        })
    }

    onRender(){

        this.camera.lookAt(this._cameraTarget.position)

        for(let i = 0; i < this._rotatingCylinders.length; i++){

            let direction = this._rotatingCylinders[i].userData.direction || Math.pow(-1, i)
            let rotationSpeed = this._rotatingCylinders[i].userData.rotationSpeed || 1

            this._rotatingCylinders[i].rotateY(ROTATION_Y_SPEED * direction * rotationSpeed)
        }

    }

    /**
     * #######################
     * #######################
     * ####################### PART HELPERS
     * #######################
     * #######################
     */

    _colorSpecificMemberBlock(matchedIndex, matchedRepeat, targetColor){

        gsap.to(this._colorForHoveredMember, {
            duration: .5,
            onUpdate: () => {
                let arr = this._cylinderMemberInstances
                let len = arr.length
        
                for(let i = 0; i < len; i++){
        
                    let instance = arr[i]
                    let context = instance.canvasContext
                    let canvas = instance.canvas
                    let wordCoordinate = instance.wordsCoordinates
                    
                    context.clearRect(0, 0, canvas.width, canvas.height)
        
                    for(let j = 0; j < wordCoordinate.length; j++){
        
                        let text = wordCoordinate[j].word.text
                        let offset = wordCoordinate[j].offset
                        let offsetY = wordCoordinate[j].offsetY
                        let font = wordCoordinate[j].word.font
        
                        let repeat = wordCoordinate[j].repeat
                        let index = wordCoordinate[j].index
        
                        if(matchedIndex === index && matchedRepeat === repeat){
                            context.fillStyle = this._colorForHoveredMember.getStyle()
                        } else {
                            context.fillStyle = "#fff"
                        }
        
                        context.font = font
                        context.fillText(
                            text, 
                            offset,
                            offsetY
                        )
                    }
        
                    instance.mesh.material.map.needsUpdate = true
                }
            },

            r: targetColor,
            g: targetColor,
            b: targetColor,
        })
    
    }

    _addToIntersectablesMesh(mesh){

        mesh.userData.events = {}

        this.intersectableObjects.push(mesh)
    }

    addToRotatingCylinders(cylinder, speed = 1){

        // reduce global speed
        let _speed = speed * .4
        
        cylinder.userData.rotationSpeed = _speed
        cylinder.userData.originalRotationSpeed = _speed
        cylinder.userData.direction = Math.pow(-1, this._rotatingCylinders.length + 1)
        this._rotatingCylinders.push(cylinder)
    }

    openLink(href, type){

        // // @DEV
        // return;

        let prefix = ""

        // get link prefix
        switch(type){
            case params.LINKS_TYPE.external:
                prefix = ""
                break
            case params.LINKS_TYPE.phone:
                prefix = "tel:"
                break
            case params.LINKS_TYPE.mail:
                prefix = "mailto:"
                break
            default:
                prefix = ""
            break
        }

        // perform new tab opening
        window.open(prefix + href)
    }

    setCursorPointer(){
        document.body.style.setProperty("cursor", "pointer", "important")
    }

    removeCursorPointer(){
        document.body.style.cursor = ""
    }

    getMouseCoord(event){
        return [
            (event.clientX * 2 - window.innerWidth) / window.innerWidth,
            (event.clientY * 2 - window.innerHeight) / window.innerHeight
        ]
    }

    /**
     * #######################
     * #######################
     * ####################### PART EVENTS
     * #######################
     * #######################
     */

    onDragMove(event){

        if(this._isDragging){

            let mouse = this.getMouseCoord(event)
            // percentage of distance between current and last mouse coord
            let distancePercentX = mouse[0] - this._dragMouseCurrent.x
            
            this.scene.rotation.y += -distancePercentX
        
            // set new start position
            this._dragMouseCurrent.set(mouse[0], mouse[1])

            // prevent clicking while drag
            if(this.hoveredMesh){
                this.hoveredMesh.userData.events.clickable = false
            }
        }
    }

    onDragStart(event){

        let mouse = this.getMouseCoord(event)
        this._dragMouseCurrent.set(mouse[0], mouse[1])

        this._isDragging = true
    }

    onDragStop(event){

        this._isDragging = false
        let mouse = this.getMouseCoord(event)
        this._dragMouseCurrent.set(mouse[0], mouse[1])
    }

    handleClickMesh(){
        
        if(this.hoveredMesh){
            
            let intersectableClass = this.hoveredMesh.userData.intersectableClass
            
            // has clicked on a link
            if(intersectableClass === INTERSECTABLE_CLASS.links){

                let href = this.hoveredMesh.userData.link
                let type = this.hoveredMesh.userData.type
                
                this.openLink(href, type)
            }

            // has cliked on a member link
            else if(intersectableClass === INTERSECTABLE_CLASS.members){

                let index = this.hoveredMesh.userData.memberIndex

                // mouse is on a word in texture - so we've got member index
                // index must me 0 so we check null or undefined
                if(index !== null || typeof index !== "undefined"){
                    
                    let mail = this.data.contacts.members[index] && this.data.contacts.members[index].mail

                    if(mail){
                        this.openLink(mail, params.LINKS_TYPE.mail)
                    }
                }
            }
        }
    }

    onMouseLeaveMesh(){

        let mesh = this.hoveredMesh
        let intersectableClass = mesh.userData.intersectableClass

        // if its pointer event then we need to get his parent which is _cylinderMemberGroup
        let targetMesh = intersectableClass === INTERSECTABLE_CLASS.members ? mesh.parent : mesh

        /** Set back speed rotation */
        gsap.to(targetMesh.userData, {
            duration: .5,

            rotationSpeed: targetMesh.userData.originalRotationSpeed
        })

        // restore opacity

        if(intersectableClass === INTERSECTABLE_CLASS.members){
            let matcheWordCoordinateItem =  mesh.userData.intersectedWordCoordinates

            if(matcheWordCoordinateItem){
                this._colorSpecificMemberBlock(matcheWordCoordinateItem.index, matcheWordCoordinateItem.repeat, 1)
            }

            // restore memberIndex
            mesh.userData.memberIndex = null
        } else {
            gsap.to(targetMesh.material.color, {
                duration: .5,
    
                r: 1,
                g: 1,
                b: 1,
            })
        }

        /** Set cursor */

        this.removeCursorPointer()

        // set it null
        super.onMouseLeaveMesh()

    }

    onMouseEnterMesh(intersect){
        super.onMouseEnterMesh(intersect)

        let mesh = intersect.object
        let intersectableClass = mesh.userData.intersectableClass

        // if its pointer event then we need to get his parent which is _cylinderMemberGroup
        let targetMesh = intersectableClass === INTERSECTABLE_CLASS.members ? mesh.parent : mesh

        /** Decrease speed rotation */
        gsap.to(targetMesh.userData, {
            duration: .5,

            rotationSpeed: targetMesh.userData.rotationSpeed * .25
        })

        // opacity
        // for members block
        if(intersectableClass === INTERSECTABLE_CLASS.members){


        } else {
            gsap.to(targetMesh.material.color, {
                duration: .5,

                r: .4,
                g: .4,
                b: .4,
            })
        }

        /** Set cursor */

        if(intersectableClass === INTERSECTABLE_CLASS.links){
            this.setCursorPointer()
        }
    }

    onMouseMoveMesh(intersect){
        super.onMouseMoveMesh(intersect)

        let mesh = intersect.object
        let intersectableClass = mesh.userData.intersectableClass

        // mouse enter an member link mesh
        if(intersectableClass === INTERSECTABLE_CLASS.members){

            // find word intersection in texture
            let intersectX = intersect.uv.x
            let wordIntersected = this._wordsCoordinates.find(f => intersectX >= f.from && intersectX <= f.to)

            // set word index in userData
            if(wordIntersected){

                let intersectedIndex = wordIntersected.index

                // has move on new word index
                if(mesh.userData.memberIndex && mesh.userData.memberIndex === intersectedIndex){
                    // console.log("same")
                } else {

                    let uv = intersect.uv
                    let matcheWordCoordinateItem = this._wordsCoordinates.find(w => uv.x >= w.from && uv.x <= w.to)
                    mesh.userData.intersectedWordCoordinates = matcheWordCoordinateItem
        
                    if(matcheWordCoordinateItem){
                        this._colorSpecificMemberBlock(matcheWordCoordinateItem.index, matcheWordCoordinateItem.repeat, .4)
                    }
                }

                mesh.userData.memberIndex = intersectedIndex
                this.setCursorPointer()
            } else {
                mesh.userData.memberIndex = null
                this.removeCursorPointer()

                // like a mouseLeaveMesh
                let matcheWordCoordinateItem =  mesh.userData.intersectedWordCoordinates

                if(matcheWordCoordinateItem){
                    this._colorSpecificMemberBlock(matcheWordCoordinateItem.index, matcheWordCoordinateItem.repeat, 1)
                }
            }
        }
    }

    _onMouseDownMesh(){

        // on desktop only
        this.data.hasOnceDrag && this.data.hasOnceDrag()


        if(this.hoveredMesh){
            this.hoveredMesh.userData.events.clickable = true
        }
    }

    _onMouseUpMesh(){
        if(this.hoveredMesh && this.hoveredMesh.userData.events.clickable){
           this.handleClickMesh()
        }
    }

    attachEvents(){
        super.attachEvents()


        window.addEventListener("mousedown", this.onDragStart)
        window.addEventListener("mousemove", this.onDragMove)
        window.addEventListener("mouseup", this.onDragStop)

        // no interaction with mesh
        if(this.data.mobileDetected) return

        // desktop
        // on mesh
        this.container.addEventListener("mousedown", this._onMouseDownMesh)
        this.container.addEventListener("mouseup", this._onMouseUpMesh)
    }

    removeEvents(){
        super.removeEvents()
        
        window.removeEventListener("mousedown", this.onDragStart)
        window.removeEventListener("mousemove", this.onDragMove)
        window.removeEventListener("mouseup", this.onDragStop)
        
        // no interaction with mesh
        if(this.data.mobileDetected) return

        // desktop
        // on mesh
        this.container.addEventListener("mousedown", this._onMouseDownMesh)
        this.container.addEventListener("mouseup", this._onMouseUpMesh)
    }

    /**
     * #######################
     * #######################
     * ####################### PART SCENE
     * #######################
     * #######################
     */

    initScene(){
        super.initScene()

        let isMobile = this.data.mobileDetected

        // set camera positionp
        // this.camera.position.set(0, -3.4, 11)
        // this.camera.position.multiplyScalar(3)

        this._buildSloganCylinders(0)

        if(isMobile){

            this._buildSloganCylinders(1)
            this._buildSloganCylinders(2)

            this.camera.position.z = -10
            
        } else {
            this._cylinderOffset += -2.5

            /** _buildLinksCylinders */
            this._buildLinksCylinders()
            this._cylinderOffset *= 1.2
    
            this._buildMembersCylinders()
        }

        // set camera
        this.camera.position.y = this._cylinderOffset / 2

        // set camera target
        this._cameraTarget.position.set(0, this.camera.position.y, -40)

        this.scene.add(this._cameraTarget)


                // /// ------
                // let oo = new THREE.Mesh(
                //     new THREE.CylinderGeometry(38, 38),
                //     new THREE.MeshBasicMaterial({
                //         color: "red",
                //         wireframe: true,
                //         side: THREE.DoubleSide
                //     })
                // )
                
                // // oo.position.set(0, 0, -30)
                // this.scene.add(oo)
                // /// ------
    }

    /** block slogan */
    _buildSloganCylinders(offset = 0){

                
        /** Build cylinder slogan */

        let letterSpacing = this.data.mobileDetected ? 7.5 : 15
        letterSpacing = 15
        let scalerFontSize = this.data.mobileDetected ? 2 : 1

        console.log("@@@@@@@@@@")
        let _sloganCylinder = new Cylinder2(
            [
                {text: SLOGAN_WORDING.weAre, fontSize: 40, fontWeight: 700, fontFamily: "GrtskPetaBold", letterSpacing: letterSpacing},
                {text: SLOGAN_WORDING.ubiquity, fontSize: 80, fontWeight: 700, fontFamily: "GrtskMegaBold"},
                {text: SLOGAN_WORDING.weCreate, fontSize: 40, fontWeight: 700, fontFamily: "GrtskPetaBold", letterSpacing: letterSpacing},
            ], 
            {
                reversed: true,
                // repeat: 2,
                quality: Math.min(2, window.devicePixelRatio || 1), // mobile with devicePixelRatio = 3 wont crash
                cylinderRadius: this.CYLINDER_RADIUS,
                ratio: this.CYLINDER_RATIO,
                radialSegments: CYLINDER_RADIAL_SEGMENTS,
                // spaceBetweenWord: 70
            }
        ).getMesh()

        this.scene.add(_sloganCylinder)
        _sloganCylinder.position.y = this._cylinderOffset
        _sloganCylinder.rotateY(offset === 2 ? Math.PI * .5 : 0)

        // make it rotate
        this.addToRotatingCylinders(_sloganCylinder)


        if(this.data.mobileDetected){
            // increment offset
            this._cylinderOffset += -_sloganCylinder.geometry.parameters.height * .85
        }
    }

    /** block links */

    _buildLinksCylinders(){

        for(let i = 0; i < this.data.contacts.links.length; i++){

            // cylinder parameters
            let link = this.data.contacts.links[i]
            let label = link.label.toUpperCase()

            let cylinder = new Cylinder2(
                [
                    {text: label, fontSize: 60, fontWeight: 700, fontFamily: "GrtskMegaBold"},
                    {text: label, fontSize: 60, fontWeight: 200, fontFamily: "GrtskMegaExtralight"},
                ],{
                    quality: Math.min(2, window.devicePixelRatio || 1), // mobile with devicePixelRatio = 3 wont crash
                    reversed: true,
                    cylinderRadius: this.CYLINDER_RADIUS,
                    ratio: this.CYLINDER_RATIO,
                    radialSegments: CYLINDER_RADIAL_SEGMENTS,
                }
            ).getMesh()

            // rotation speed
            this


            cylinder.position.y = this._cylinderOffset
            this.scene.add(cylinder)

            // make it rotate
            this.addToRotatingCylinders(cylinder)

            // is intersectable - with action
            cylinder.userData.link = link.href
            cylinder.userData.type = link.type
            cylinder.userData.intersectableClass = INTERSECTABLE_CLASS.links
            this._addToIntersectablesMesh(cylinder)

            // set next offset Y
            this._cylinderOffset += -cylinder.geometry.parameters.height * 1.2
        }
    }
    

    /** block pointer event */

    _buildPointerEventCylinder(referenceCylinderInstance){
        let pointerEventCylinderHeight = referenceCylinderInstance.mesh.geometry.parameters.height
        // it takes 3 rows height
        pointerEventCylinderHeight *= 3
        // padding vertical
        pointerEventCylinderHeight *= 1.4

        let cylGeometry = new THREE.CylinderBufferGeometry(
            referenceCylinderInstance.mesh.geometry.parameters.radiusTop,
            referenceCylinderInstance.mesh.geometry.parameters.radiusBottom,
            pointerEventCylinderHeight,
            32,
            1,
            true
        )

        this._cylinderPointerEvent = new THREE.Mesh(
            cylGeometry,
            new THREE.MeshBasicMaterial({
                color: "green",
                wireframe: true,
                side: THREE.BackSide,
                visible: false
            })
        )

        this._cylinderPointerEvent.name = CYLINDER_MEMBER_POINTER_EVENT_NAME

        this._cylinderPointerEvent.scale.x = -1
        this._cylinderPointerEvent.position.y = this._cylinderOffsetInMembers
        this._cylinderMembersGroup.add(this._cylinderPointerEvent)


        // is intersectable
        this._cylinderPointerEvent.userData.intersectableClass = INTERSECTABLE_CLASS.members
        this._cylinderPointerEvent.userData.wordIndex = null
        this._cylinderPointerEvent.userData.memberIndex = null
        this._addToIntersectablesMesh(this._cylinderPointerEvent)
    }

    /** block row */

    _buildRowCylinder(key, customDrawing){
        
        

        let text = this.data.contacts.members.map(m => ({
            text: m[key].toUpperCase(), 
            fontSize: 30,
            ...TYPOGRAPHY_FOR_ROW[key]
        }))
        
        let cylinderRowInstance = new Cylinder2(
            text,
            {
                quality: Math.min(2, window.devicePixelRatio || 1), // mobile with devicePixelRatio = 3 wont crash
                reversed: true,
                cylinderRadius: this.CYLINDER_RADIUS,
                ratio: this.CYLINDER_RATIO,
                repeat: 4,
                // spaceBetweenWord: 600,
                customDrawing,
                radialSegments: CYLINDER_RADIAL_SEGMENTS,
            }
        )

        let cylinderRowMesh = cylinderRowInstance.getMesh()
        cylinderRowMesh.position.y = this._cylinderOffsetInMembers

        // belongs to members cylinder groups
        this._cylinderMembersGroup.add(cylinderRowMesh)

        this._cylinderMemberInstances.push(cylinderRowInstance)

        return cylinderRowInstance
    }

    /** block members */

    _buildMembersCylinders(){
        

        // cylinder member group
        this._cylinderMembersGroup.position.y = this._cylinderOffset
        this.scene.add(this._cylinderMembersGroup)
        
        // make it rotate
        this.addToRotatingCylinders(this._cylinderMembersGroup, .5)

        

        /** Create rows cylinders */

        let nameCylinderRowInstance = this._buildRowCylinder("fullname")

        /** Center members cylinder */
        this._centerCylinderMembers(nameCylinderRowInstance)
        
        this._cylinderOffsetInMembers += -nameCylinderRowInstance.mesh.geometry.parameters.height * 1.2
        /** Create Pointer event cylinder */
        this._buildPointerEventCylinder(nameCylinderRowInstance)

        /** set wordCoordinateReference */
        this._wordsCoordinates = nameCylinderRowInstance.wordsCoordinates

        /** custom drawing allow to align center row accroding nameCylinderRowInstance */
        const customDrawingCenter = (wordIndex, repeat, offset, dimension) => {

            let _offset = offset
            let wordMappingReference = this._wordsCoordinates.find(w => w.index === wordIndex && w.repeat === repeat)

            if(wordMappingReference){
                _offset = wordMappingReference.offset + wordMappingReference.dimension.width * .5 - dimension.width * .5
            }

            return _offset
        }

        let jobCylinderRowInstance = this._buildRowCylinder("job", customDrawingCenter)
        this._cylinderOffsetInMembers += -jobCylinderRowInstance.mesh.geometry.parameters.height * 1.2

        let mailCylinderRowInstance = this._buildRowCylinder("mail", customDrawingCenter)
    }

    _centerCylinderMembers(instanceReference){


        let wordsCoordinates = instanceReference.wordsCoordinates

        // right-coord of last word of the first repeat
        // represent percent width of first repeat
        let lastTo = wordsCoordinates[instanceReference.words.length - 1].to
        let widthPercent = lastTo

        this._cylinderMembersGroup.rotation.y = Math.PI + Math.PI * widthPercent
        
        // let foo = new THREE.Mesh(
        //     new THREE.CylinderBufferGeometry(
        //         instanceReference.mesh.geometry.parameters.radiusTop,
        //         instanceReference.mesh.geometry.parameters.radiusBottom,
        //         instanceReference.mesh.geometry.parameters.height,
        //         32,
        //         1,
        //         true,
        //         0,
        //         Math.PI * 2 * widthPercent
        //     ),
        //     new THREE.MeshBasicMaterial({color: "orange", wireframe: true})
        // )

        // foo.rotation.y = Math.PI + (Math.PI * .5)
        // foo.scale.x = -1

        // foo.position.y = -10
        // this.scene.add(foo)

    }
}